import { motion, AnimatePresence } from "framer-motion";
import { Bell, Wifi, Battery, Signal } from "lucide-react";

export default function PhoneMockup({
  notification,
  actions,
  onAction,
  showNotification,
  showActions,
  actionTaken,
  className = "",
}) {
  const badgeColor =
    notification?.badge === "Failed"
      ? "#EF4444"
      : notification?.badge === "Complete"
      ? "#10B981"
      : "#F59E0B";

  return (
    <div className={`relative ${className}`}>
      {/* iPhone frame */}
      <div
        className="relative rounded-[2.5rem] overflow-hidden"
        style={{
          width: "260px",
          height: "520px",
          background: "#000",
          border: "3px solid rgba(255,255,255,0.15)",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.05)",
        }}
      >
        {/* Notch */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 z-20"
          style={{
            width: "120px",
            height: "28px",
            background: "#000",
            borderRadius: "0 0 20px 20px",
          }}
        />

        {/* Status bar */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-3">
          <span className="text-xs font-medium" style={{ color: "#fff", fontSize: "11px" }}>
            9:41
          </span>
          <div className="flex items-center gap-1">
            <Signal className="w-3 h-3 text-white" />
            <Wifi className="w-3 h-3 text-white" />
            <Battery className="w-4 h-3 text-white" />
          </div>
        </div>

        {/* Screen content */}
        <div
          className="relative z-10 h-full px-4 pt-4"
          style={{ background: "linear-gradient(180deg, #0A0A0A 0%, #111 100%)" }}
        >
          {/* App header */}
          <div className="flex items-center justify-between mb-4 pt-4">
            <div>
              <h4
                className="text-sm font-semibold text-white"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                runzr
              </h4>
              <p className="text-xs" style={{ color: "#71717A" }}>
                Active Sessions
              </p>
            </div>
            <div className="relative">
              <Bell className="w-4 h-4" style={{ color: "#A1A1AA" }} />
              {showNotification && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                  style={{ background: badgeColor }}
                />
              )}
            </div>
          </div>

          {/* Notification card */}
          <AnimatePresence>
            {showNotification && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                data-testid="phone-notification"
                className="rounded-xl p-3 mb-3"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-md flex items-center justify-center"
                      style={{ background: "rgba(255,255,255,0.1)" }}
                    >
                      <span className="text-xs" style={{ fontFamily: "'JetBrains Mono'" }}>
                        R
                      </span>
                    </div>
                    <span className="text-xs font-medium text-white">
                      {notification?.title}
                    </span>
                  </div>
                  <span
                    className="text-xs px-1.5 py-0.5 rounded"
                    style={{
                      background: `${badgeColor}20`,
                      color: badgeColor,
                      fontFamily: "'JetBrains Mono'",
                      fontSize: "9px",
                      textTransform: "uppercase",
                    }}
                  >
                    {notification?.badge}
                  </span>
                </div>
                <p
                  className="text-xs leading-relaxed mb-3"
                  style={{ color: "#A1A1AA" }}
                >
                  {notification?.body}
                </p>

                {/* Action buttons */}
                <AnimatePresence>
                  {showActions && !actionTaken && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="flex gap-2"
                    >
                      {actions?.map((action, i) => (
                        <button
                          key={action}
                          onClick={() => onAction?.(action)}
                          data-testid={`phone-action-${action.toLowerCase().replace(/\s/g, "-")}`}
                          className="flex-1 py-2 rounded-lg text-xs font-medium transition-all duration-200"
                          style={{
                            background: i === 0 ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
                            color: i === 0 ? "#fff" : "#A1A1AA",
                            border: `1px solid ${i === 0 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)"}`,
                          }}
                        >
                          {action}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {actionTaken && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-xs py-2 text-center rounded-lg"
                    style={{
                      background: "rgba(16, 185, 129, 0.1)",
                      color: "#10B981",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                    }}
                  >
                    {actionTaken} sent
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Session info */}
          <div
            className="rounded-xl p-3"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-white">Live Session</span>
              <div className="flex items-center gap-1">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: badgeColor }}
                />
                <span
                  className="text-xs"
                  style={{
                    color: badgeColor,
                    fontFamily: "'JetBrains Mono'",
                    fontSize: "9px",
                    textTransform: "uppercase",
                  }}
                >
                  {notification?.badge || "Running"}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#71717A" }}>Duration</span>
                <span className="text-xs" style={{ color: "#A1A1AA", fontFamily: "'JetBrains Mono'" }}>12m 34s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#71717A" }}>Files changed</span>
                <span className="text-xs" style={{ color: "#A1A1AA", fontFamily: "'JetBrains Mono'" }}>14</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#71717A" }}>Tests</span>
                <span className="text-xs" style={{ color: "#A1A1AA", fontFamily: "'JetBrains Mono'" }}>3/3</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
